import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Plus, Trash2, Save, X, Edit3 } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  title: '', slug: '', short_description: '', description: '',
  icon: '', price: '', duration: '', features: '',
};

export default function AdminServices() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service_id when editing, 'new' when creating
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/services`);
      setServices(res.data);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  };

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const startCreate = () => {
    setForm(emptyForm);
    setEditing('new');
  };

  const startEdit = (svc) => {
    setForm({
      title: svc.title || '',
      slug: svc.slug || '',
      short_description: svc.short_description || '',
      description: svc.description || '',
      icon: svc.icon || '',
      price: svc.price || '',
      duration: svc.duration || '',
      features: Array.isArray(svc.features) ? svc.features.join('\n') : (svc.features || ''),
    });
    setEditing(svc.service_id);
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyForm); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
        features: form.features ? form.features.split('\n').map(s => s.trim()).filter(Boolean) : [],
      };
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/admin/services`, payload, { withCredentials: true });
        toast.success('Service created');
      } else {
        await axios.patch(`${API_URL}/api/admin/services/${editing}`, payload, { withCredentials: true });
        toast.success('Service updated');
      }
      cancelEdit();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (svc) => {
    if (!window.confirm(`Delete service "${svc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/services/${svc.service_id}`, { withCredentials: true });
      toast.success('Service deleted');
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <AdminLayout title="Services">
      <div data-testid="admin-services-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1C]">Services</h1>
            <p className="text-sm text-[#475569]">Edit the services that appear on the public Services page.</p>
          </div>
          {!editing && (
            <Button onClick={startCreate} className="btn-primary-orange" data-testid="new-service-btn">
              <Plus size={14} className="mr-2" /> New Service
            </Button>
          )}
        </div>

        {/* Editor */}
        {editing && (
          <form onSubmit={handleSave} className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6 shadow-sm" data-testid="service-editor">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[#0A0F1C]">{editing === 'new' ? 'Create Service' : 'Edit Service'}</h2>
              <button type="button" onClick={cancelEdit} className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#475569]">Title</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="svc-title" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#475569]">URL Slug (auto-generated if blank)</label>
                <Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder={slugify(form.title)} data-testid="svc-slug" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#475569]">Icon (lucide name, optional)</label>
                <Input value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} placeholder="rocket, briefcase, etc." data-testid="svc-icon" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#475569]">Short Description</label>
                <Input value={form.short_description} onChange={e => setForm({...form, short_description: e.target.value})} placeholder="One-liner shown on the services grid" data-testid="svc-short" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#475569]">Full Description</label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} data-testid="svc-desc" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#475569]">Price</label>
                <Input value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="$497 or Custom" data-testid="svc-price" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#475569]">Duration</label>
                <Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} placeholder="60 minutes, 4 weeks, etc." data-testid="svc-duration" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#475569]">Features (one per line)</label>
                <Textarea value={form.features} onChange={e => setForm({...form, features: e.target.value})} rows={4} placeholder="LLC formation guidance&#10;EIN registration&#10;Business banking setup" data-testid="svc-features" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button type="button" onClick={cancelEdit} className="btn-outline flex-1" disabled={saving}>Cancel</Button>
              <Button type="submit" className="btn-primary-blue flex-1" disabled={saving} data-testid="save-svc-btn">
                <Save size={14} className="mr-2" /> {saving ? 'Saving...' : 'Save Service'}
              </Button>
            </div>
          </form>
        )}

        {/* Services list */}
        {loading ? (
          <div className="text-center py-10"><div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : services.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-10 text-center">
            <Settings size={32} className="text-[#CBD5E1] mx-auto mb-3" />
            <p className="text-[#475569]">No services yet. Click "New Service" to create one.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((svc) => (
              <div key={svc.service_id} className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`svc-card-${svc.service_id}`}>
                <div className="font-bold text-[#0A0F1C] mb-1">{svc.title}</div>
                <div className="text-xs text-[#94A3B8] mb-2">/{svc.slug}</div>
                <p className="text-sm text-[#475569] mb-3 line-clamp-3">{svc.short_description || svc.description}</p>
                {(svc.price || svc.duration) && (
                  <div className="text-xs text-[#475569] mb-3">
                    {svc.price && <span className="font-semibold text-[#0A0F1C]">{svc.price}</span>}
                    {svc.price && svc.duration && <span> · </span>}
                    {svc.duration && <span>{svc.duration}</span>}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={() => startEdit(svc)} className="btn-outline flex-1 text-xs" data-testid={`edit-svc-${svc.service_id}`}>
                    <Edit3 size={12} className="mr-1.5" /> Edit
                  </Button>
                  <Button onClick={() => handleDelete(svc)} className="bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 flex-1 text-xs font-semibold" data-testid={`del-svc-${svc.service_id}`}>
                    <Trash2 size={12} className="mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
